import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoCpf,
  CampoTelefone,
  Modal,
  useDialogo,
} from '../../componentes/ui';
import { requisicaoAutenticada } from '../../servicos/http';
import { useConsultaDeCpf, type PessoaEncontrada } from '../../hooks/useConsultaDeCpf';
import type { Colaborador, EmpresaResumo } from '../../servicos/empresas';
import type { ColaboradorCriado } from '../../servicos/escrita';
import { formatarCpf } from '../../servicos/formato';
import { useEditarColaborador } from '../../hooks/useEscrita';
import { useIdentidade } from '../../hooks/useSessao';
import { ErroDaApi } from '../ErroDaApi';
import { SeletorDeEmpresa } from '../SeletorDeEmpresa';
import { SeletorDeVinculos } from '../SeletorDeVinculos';
import {
  formatarTelefone,
  juntarVinculos,
  separarVinculos,
  vinculosForaDaLista,
} from '../vinculos-trabalhistas';

/**
 * Cadastro e edição de colaborador, no molde do Deca 1 (`EmpresaEmployers.js`).
 *
 * ## O que mudou em relação ao formulário anterior do V2
 *
 * - **Vínculo trabalhista** é escolha múltipla numa lista fechada, não texto
 *   livre "Cargo". O cronograma acha destinatários por esses rótulos; um cargo
 *   digitado à mão nunca casava com "Decisor" nem "Contábil".
 * - **Telefone** entrou, opcional. A API já gravava; a tela não pedia.
 * - **Senha inicial** pode ser digitada, como no Deca 1, para o operador
 *   combinar com a pessoa. Ela **não vai por e-mail** e não aparece na tela
 *   depois de salvar.
 * - **E-mail de acesso** é opcional e vem desmarcado. Marcado, a pessoa recebe
 *   um **link para criar a própria senha** (7 dias, uso único) — nunca a senha.
 *
 * ## Sem `businessId`, a empresa é escolhida aqui — e pode ficar de fora
 *
 * A tela de Pessoas abre este mesmo formulário sem empresa definida, de dois
 * jeitos:
 *
 * - **Adicionar pessoa**: a empresa é opcional. Sem ela, a pessoa entra só na
 *   agenda (`POST /business/pessoas`) — sem conta e sem acesso, porque login
 *   sem empresa não leva a lugar nenhum. Vínculo trabalhista, acesso e
 *   permissões são do vínculo, e por isso só aparecem com empresa escolhida.
 * - **Vincular a uma empresa** (`pessoa` informada): quem já está na agenda
 *   ganha o vínculo. A empresa é obrigatória e os dados pessoais vêm prontos —
 *   o cadastro de colaborador acha a pessoa pelo CPF e a reaproveita.
 */

type ColaboradorCriadoComVinculo = ColaboradorCriado & {
  /** `true` quando o e-mail já tinha conta: nada de senha, só aviso de vínculo. */
  readonly contaJaExistia?: boolean;
};

interface FormDeCadastro {
  taxpayerId: string;
  fullName: string;
  phone: string;
  vinculos: string[];
  emailAddress: string;
  senha: string;
  gestor: boolean;
  assinaPelaEmpresa: boolean;
  enviarCredenciais: boolean;
}

const FORM_VAZIO: FormDeCadastro = {
  taxpayerId: '',
  fullName: '',
  phone: '',
  vinculos: [],
  emailAddress: '',
  senha: '',
  gestor: false,
  assinaPelaEmpresa: false,
  // Desmarcado: o e-mail sai quando o operador escolhe mandar.
  enviarCredenciais: false,
};

/** O formulário já preenchido com quem está na agenda. */
function formDaPessoa(pessoa: PessoaEncontrada | undefined): FormDeCadastro {
  if (pessoa === undefined) return FORM_VAZIO;
  return {
    ...FORM_VAZIO,
    taxpayerId: pessoa.cpf ?? '',
    fullName: pessoa.nome,
    emailAddress: pessoa.emailAddress,
    phone: pessoa.telefone === null ? '' : formatarTelefone(pessoa.telefone),
  };
}

export function CadastroDeColaborador(props: {
  /** Ausente = a empresa é escolhida no próprio formulário. */
  readonly businessId?: string;
  /** Quem já está na agenda e vai ganhar o vínculo. Só faz sentido sem `businessId`. */
  readonly pessoa?: PessoaEncontrada;
  readonly aberto: boolean;
  readonly aoFechar: () => void;
  /** "Gerar link" no cabeçalho, como o drawer do Deca 1 — abre o convite. */
  readonly aoGerarLink?: () => void;
  /**
   * `false` para quem gerencia a empresa pela carteira (o assessor): gestor e
   * signatário são nomeados pela própria empresa ou pela operação, e a API
   * recusa o resto. Ausente = pode.
   */
  readonly podeDefinirPapeis?: boolean;
}): JSX.Element {
  const definePapeis = props.podeDefinirPapeis !== false;
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormDeCadastro>(() => formDaPessoa(props.pessoa));
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const escolheEmpresa = props.businessId === undefined;
  /** "Adicionar pessoa": sem empresa, a pessoa fica só na agenda. */
  const empresaOpcional = escolheEmpresa && props.pessoa === undefined;
  const [empresa, setEmpresa] = useState<EmpresaResumo | null>(null);
  const businessId = props.businessId ?? empresa?.id ?? null;
  /** Vínculo trabalhista, acesso e permissões são do vínculo: sem empresa, não há. */
  const comVinculo = businessId !== null;

  /*
    RN-311 — o CPF responde "esta pessoa já tem cadastro?" enquanto se digita.
    O formulário dizia que o cadastro seria reaproveitado, mas quem digitava o
    CPF de alguém já cadastrado continuava preenchendo nome e e-mail à mão — e
    um deles saía diferente do que estava no banco.

    O preenchimento só cobre o que está **em branco**: o que o operador
    escreveu fica, porque ele pode estar justamente corrigindo o cadastro.
  */
  const consulta = useConsultaDeCpf(form.taxpayerId, props.aberto);
  /** Pessoa cadastrada **e com conta**: a senha é a que ela já usa. */
  const contaExistente = consulta.pessoa?.temAcesso === true;

  useEffect(() => {
    const pessoa = consulta.pessoa;
    if (pessoa === null) return;
    setForm((atual) => ({
      ...atual,
      fullName: atual.fullName.trim() === '' ? pessoa.nome : atual.fullName,
      emailAddress: atual.emailAddress.trim() === '' ? pessoa.emailAddress : atual.emailAddress,
      phone:
        atual.phone.trim() === '' && pessoa.telefone !== null
          ? formatarTelefone(pessoa.telefone)
          : atual.phone,
    }));
  }, [consulta.pessoa]);

  const criar = useMutation({
    mutationFn: ({ dados, empresaId }: { dados: FormDeCadastro; empresaId: string }) =>
      requisicaoAutenticada<ColaboradorCriadoComVinculo>(`/business/${empresaId}/colaboradores`, {
        metodo: 'POST',
        corpo: {
          fullName: dados.fullName,
          emailAddress: dados.emailAddress,
          ...(dados.taxpayerId.replace(/\D/g, '') === '' ? {} : { taxpayerId: dados.taxpayerId }),
          ...(dados.phone.trim() === '' ? {} : { phone: dados.phone }),
          ...(dados.vinculos.length === 0 ? {} : { occupation: dados.vinculos }),
          // Conta que já existe mantém a senha dela: o servidor descarta a
          // que viesse daqui, e mandar mesmo assim só criaria a dúvida de
          // ter trocado a senha de alguém.
          ...(dados.senha === '' || contaExistente ? {} : { senha: dados.senha }),
          gestor: dados.gestor,
          assinaPelaEmpresa: dados.assinaPelaEmpresa,
          enviarCredenciais: dados.enviarCredenciais,
        },
      }),
    onSuccess: (_criado, { empresaId }) => {
      void queryClient.invalidateQueries({ queryKey: ['colaboradores', empresaId] });
      void queryClient.invalidateQueries({ queryKey: ['pessoas'] });
    },
  });

  /** Sem empresa: só a pessoa, na agenda. A conta nasce quando houver vínculo. */
  const criarNaAgenda = useMutation({
    mutationFn: (dados: FormDeCadastro) =>
      requisicaoAutenticada<PessoaEncontrada>('/business/pessoas', {
        metodo: 'POST',
        corpo: {
          fullName: dados.fullName,
          emailAddress: dados.emailAddress,
          taxpayerId: dados.taxpayerId,
          ...(dados.phone.trim() === '' ? {} : { phone: dados.phone }),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pessoas'] });
    },
  });

  /**
   * CPF de quem já está na agenda, sem empresa escolhida: não há o que
   * cadastrar — o servidor recusaria a duplicata. O aviso oferece o caminho.
   */
  const jaNaAgenda = empresaOpcional && !comVinculo && consulta.pessoa !== null;

  const cadastrar = (): void => {
    if (businessId !== null) criar.mutate({ dados: form, empresaId: businessId });
    else if (empresaOpcional && !jaNaAgenda) criarNaAgenda.mutate(form);
  };

  const fechar = (): void => {
    criar.reset();
    criarNaAgenda.reset();
    setForm(formDaPessoa(props.pessoa));
    setMostrarSenha(false);
    setEmpresa(null);
    props.aoFechar();
  };

  const criado = criar.data;
  const naAgenda = criarNaAgenda.data;

  // Mudam de lugar conforme o modo: com empresa fixa, o e-mail é do acesso e o
  // vínculo trabalhista vai com os dados pessoais; escolhendo empresa, o e-mail
  // é contato e o vínculo trabalhista vai junto com a empresa.
  const campoDeEmail = (
    <Campo
      rotulo="E-mail"
      type="email"
      obrigatorio
      required
      autoComplete="off"
      readOnly={contaExistente}
      ajuda={
        contaExistente
          ? 'É o login que a pessoa já usa; o vínculo entra nessa mesma conta.'
          : escolheEmpresa && !comVinculo
            ? 'Contato da pessoa. Vira o login se ela for vinculada a uma empresa.'
            : 'Vira o login da pessoa.'
      }
      value={form.emailAddress}
      onChange={(e) => setForm({ ...form, emailAddress: e.target.value })}
    />
  );
  const seletorDeVinculos = (
    <SeletorDeVinculos
      escolhidos={form.vinculos}
      aoMudar={(v) => setForm({ ...form, vinculos: v })}
    />
  );

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo={
        props.pessoa !== undefined
          ? 'Vincular a uma empresa'
          : escolheEmpresa
            ? 'Adicionar pessoa'
            : 'Cadastrar colaborador'
      }
      descricao={
        props.pessoa !== undefined
          ? `${props.pessoa.nome} passa a ter acesso à empresa escolhida. Quem já tem conta é só vinculado.`
          : escolheEmpresa
            ? 'A empresa é opcional: sem ela, a pessoa fica só na agenda, sem acesso ao sistema.'
            : 'O acesso pode ir por e-mail, com um link para a pessoa criar a senha. Quem já tem conta em outra empresa é só vinculado.'
      }
      largura="larga"
      rodape={
        criado === undefined && naAgenda === undefined ? (
          <>
            {props.aoGerarLink !== undefined && (
              <Botao
                variante="sutil"
                className="mr-auto"
                onClick={() => {
                  fechar();
                  props.aoGerarLink?.();
                }}
              >
                Gerar link de cadastro
              </Botao>
            )}
            <Botao variante="secundario" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              onClick={cadastrar}
              disabled={(!comVinculo && !empresaOpcional) || jaNaAgenda}
              carregando={criar.isPending || criarNaAgenda.isPending}
            >
              {props.pessoa !== undefined
                ? 'Vincular'
                : empresaOpcional && !comVinculo
                  ? 'Cadastrar sem empresa'
                  : 'Cadastrar'}
            </Botao>
          </>
        ) : (
          <Botao onClick={fechar}>Concluir</Botao>
        )
      }
    >
      {criado !== undefined ? (
        <ResultadoDoCadastro
          criado={criado}
          senhaDefinida={(criar.variables?.dados.senha ?? '') !== ''}
        />
      ) : naAgenda !== undefined ? (
        <Aviso tom="positivo" titulo={`${naAgenda.nome} está na agenda`}>
          Sem empresa e sem acesso ao sistema. Para vincular a uma empresa, abra o cadastro dela na
          lista de Pessoas.
        </Aviso>
      ) : (
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            cadastrar();
          }}
        >
          <fieldset className="flex flex-col gap-4">
            <legend className="mb-3 text-xs uppercase tracking-wide text-conteudo-sutil">
              Informações pessoais
            </legend>
            {props.pessoa !== undefined ? (
              <ResumoDaPessoa pessoa={props.pessoa} />
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  {/*
                    Colaborador de uma empresa pode ficar sem CPF (27/10): o
                    e-mail identifica a pessoa. Sem empresa, na agenda, o CPF
                    continua obrigatório — é por ele que ela é achada depois.
                  */}
                  <CampoCpf
                    rotulo="CPF"
                    obrigatorio={!comVinculo}
                    required={!comVinculo}
                    ajuda={
                      consulta.consultando
                        ? 'Procurando cadastro…'
                        : comVinculo
                          ? 'Opcional. Se a pessoa já existir, o cadastro é reaproveitado.'
                          : 'Se a pessoa já existir, o cadastro é reaproveitado.'
                    }
                    value={form.taxpayerId}
                    onChange={(valor) => setForm({ ...form, taxpayerId: valor })}
                  />
                  <CampoTelefone
                    rotulo="Telefone"
                    ajuda="Opcional."
                    value={form.phone}
                    onChange={(valor) => setForm({ ...form, phone: valor })}
                  />
                </div>
                <Campo
                  rotulo="Nome completo"
                  obrigatorio
                  required
                  autoComplete="name"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                />
                {/* Na agenda o e-mail é contato; vira login só quando houver empresa. */}
                {escolheEmpresa && campoDeEmail}
                {consulta.pessoa !== null &&
                  (jaNaAgenda ? (
                    <Aviso tom="informacao" titulo="Esta pessoa já está na agenda">
                      {consulta.pessoa.nome} ({consulta.pessoa.emailAddress}) já tem cadastro
                      {consulta.pessoa.vinculos.length > 0 &&
                        ` — hoje em ${consulta.pessoa.vinculos.map((v) => v.razaoSocial).join(', ')}`}
                      . Escolha uma empresa abaixo para vinculá-la, ou{' '}
                      <Link
                        to={`/pessoas/${consulta.pessoa.personId}`}
                        onClick={fechar}
                        className="underline underline-offset-2"
                      >
                        abra o cadastro dela
                      </Link>
                      .
                    </Aviso>
                  ) : (
                    <Aviso tom="informacao" titulo="Esta pessoa já tem cadastro">
                      {consulta.pessoa.nome} ({consulta.pessoa.emailAddress}) já está no sistema
                      {consulta.pessoa.vinculos.length > 0 &&
                        ` — hoje em ${consulta.pessoa.vinculos.map((v) => v.razaoSocial).join(', ')}`}
                      . Os dados vieram preenchidos; cadastrar apenas vincula a pessoa a esta
                      empresa, sem criar conta nova e sem mexer na senha dela.
                    </Aviso>
                  ))}
              </>
            )}

            {!escolheEmpresa && seletorDeVinculos}
          </fieldset>

          {escolheEmpresa && (
            <fieldset className="flex flex-col gap-4 border-t border-borda pt-4">
              <legend className="mb-3 text-xs uppercase tracking-wide text-conteudo-sutil">
                Empresa
              </legend>
              <SeletorDeEmpresa
                escolhida={empresa}
                aoEscolher={setEmpresa}
                opcional={empresaOpcional}
                ajuda={
                  empresaOpcional
                    ? 'Opcional. Sem empresa, a pessoa fica só na agenda — dá para vincular depois, no cadastro dela.'
                    : 'Onde a pessoa vai trabalhar.'
                }
              />
              {comVinculo && seletorDeVinculos}
            </fieldset>
          )}

          {/*
            Conta que já existe não ganha senha nova.
            A pessoa entra com a senha que já usa na outra empresa, e o
            servidor descarta a que viesse daqui (`contaJaExistia`) — oferecer
            o campo prometia uma troca de senha que não acontece. O que sobra é
            o aviso de vínculo, que é o e-mail que ela de fato recebe.
          */}
          {comVinculo && (
            <fieldset className="flex flex-col gap-4 border-t border-borda pt-4">
              <legend className="mb-3 text-xs uppercase tracking-wide text-conteudo-sutil">
                Acesso ao sistema
              </legend>
              {!escolheEmpresa && campoDeEmail}

              {!contaExistente && (
                <Campo
                  rotulo="Senha inicial"
                  type={mostrarSenha ? 'text' : 'password'}
                  autoComplete="new-password"
                  ajuda="Opcional, para você combinar com a pessoa. Não vai por e-mail e não aparece aqui depois de salvar."
                  value={form.senha}
                  onChange={(e) => setForm({ ...form, senha: e.target.value })}
                  sufixo={
                    <button
                      type="button"
                      className="text-xs text-conteudo-secundario underline-offset-2 hover:underline"
                      aria-pressed={mostrarSenha}
                      onClick={() => setMostrarSenha((v) => !v)}
                    >
                      {mostrarSenha ? 'Ocultar' : 'Mostrar'}
                    </button>
                  }
                />
              )}

              <Caixa
                rotulo={
                  contaExistente ? 'Avisar por e-mail' : 'Enviar link para criar a senha por e-mail'
                }
                ajuda={
                  contaExistente
                    ? 'A pessoa recebe o aviso de que passou a ter acesso a esta empresa. A senha dela não muda.'
                    : 'A pessoa recebe um link para criar a própria senha — vale 7 dias e só uma vez. A senha inicial acima, se houver, vale até ela criar a dela.'
                }
                marcado={form.enviarCredenciais}
                aoMudar={(v) => setForm({ ...form, enviarCredenciais: v })}
              />

              {!contaExistente && form.senha === '' && !form.enviarCredenciais && (
                <Aviso tom="informacao" titulo="A pessoa ainda não vai conseguir entrar">
                  Sem senha inicial e sem o link, o acesso fica para depois: mande o link pela
                  edição do colaborador quando quiser liberar — ou a pessoa usa “Esqueci minha
                  senha” na tela de entrada.
                </Aviso>
              )}
            </fieldset>
          )}

          {comVinculo && !definePapeis && (
            <p className="border-t border-borda pt-4 text-xs text-conteudo-sutil">
              Entra como colaborador. Gestor e quem assina pela empresa são definidos pela própria
              empresa ou pela operação.
            </p>
          )}

          {comVinculo && definePapeis && (
            <fieldset className="flex flex-col gap-3 border-t border-borda pt-4">
              <legend className="mb-3 text-xs uppercase tracking-wide text-conteudo-sutil">
                Permissões
              </legend>
              <Caixa
                rotulo="Gestor da empresa"
                ajuda="Gestor administra o cadastro, inclusive outros colaboradores; colaborador apenas opera."
                marcado={form.gestor}
                aoMudar={(v) => setForm({ ...form, gestor: v })}
              />
              <Caixa
                rotulo="Assina pela empresa"
                ajuda="Entra como signatário do contrato na assinatura eletrônica."
                marcado={form.assinaPelaEmpresa}
                aoMudar={(v) => setForm({ ...form, assinaPelaEmpresa: v })}
              />
            </fieldset>
          )}

          {criar.isError && <ErroDaApi titulo="Não foi possível cadastrar" erro={criar.error} />}
          {criarNaAgenda.isError && (
            <ErroDaApi titulo="Não foi possível cadastrar" erro={criarNaAgenda.error} />
          )}

          {/* Submissão pelo Enter; o botão visível está no rodapé do modal. */}
          <button type="submit" className="sr-only">
            Cadastrar
          </button>
        </form>
      )}
    </Modal>
  );
}

/** Quem vai ser vinculado — só leitura: o cadastro dela se edita em outro lugar. */
function ResumoDaPessoa(props: { readonly pessoa: PessoaEncontrada }): JSX.Element {
  const p = props.pessoa;
  const itens: readonly (readonly [string, string])[] = [
    ['Nome', p.nome],
    ['CPF', p.cpf === null ? 'Sem CPF' : formatarCpf(p.cpf)],
    ['E-mail', p.emailAddress],
    ['Telefone', p.telefone === null ? '—' : formatarTelefone(p.telefone)],
  ];

  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {itens.map(([rotulo, valor]) => (
        <div key={rotulo} className="flex flex-col gap-0.5">
          <dt className="text-xs text-conteudo-sutil">{rotulo}</dt>
          <dd className="text-sm text-conteudo-primario">{valor}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * O que aconteceu depois de salvar — sem a senha, em nenhum caso.
 *
 * Os desfechos que a tela precisa distinguir: a pessoa já tinha conta (só o
 * vínculo foi criado, senha continua a dela); conta nova com o link enviado;
 * conta nova com o e-mail que **não saiu** — no legado essa falha era engolida
 * e a pessoa descobria ligando (dívida #16); e conta nova sem e-mail pedido,
 * que não é falha: é o operador combinando o acesso de outro jeito.
 */
function ResultadoDoCadastro(props: {
  readonly criado: ColaboradorCriadoComVinculo;
  /** O operador digitou a senha inicial — é por ela que a pessoa entra. */
  readonly senhaDefinida: boolean;
}): JSX.Element {
  const { criado } = props;
  const naoPedido = criado.credenciais.solicitado === false;

  return (
    <div className="flex flex-col gap-4">
      <Aviso tom="positivo" titulo={`${criado.fullName} foi cadastrado`}>
        O vínculo com a empresa está ativo como <strong>{criado.papel}</strong>.
      </Aviso>

      {criado.contaJaExistia === true ? (
        <Aviso tom="informacao" titulo="A pessoa já tinha acesso ao Deca">
          A senha dela continua a mesma.{' '}
          {naoPedido
            ? 'Nenhum aviso foi enviado.'
            : criado.credenciais.enviado
              ? `Avisamos ${criado.emailAddress} que o acesso agora inclui esta empresa.`
              : 'O aviso por e-mail não saiu — combine com a pessoa diretamente.'}
        </Aviso>
      ) : naoPedido ? (
        <Aviso tom="informacao" titulo="Nenhum e-mail foi enviado">
          {props.senhaDefinida
            ? 'A pessoa entra com a senha inicial que você definiu — combine com ela por um canal seguro.'
            : 'Quando quiser liberar o acesso, mande o link para criar a senha pela edição do colaborador — ou a pessoa usa “Esqueci minha senha” na tela de entrada.'}
        </Aviso>
      ) : criado.credenciais.enviado ? (
        <Aviso tom="informacao" titulo="Link enviado">
          {criado.emailAddress} recebeu o link para criar a senha. Ele vale por 7 dias e só uma vez.
        </Aviso>
      ) : (
        <Aviso tom="atencao" titulo="O e-mail de acesso não saiu">
          O colaborador está cadastrado, mas <strong>não recebeu o link</strong>.
          {criado.credenciais.motivo !== undefined && <> Motivo: {criado.credenciais.motivo}.</>}{' '}
          Mande de novo pela edição do colaborador depois de resolver.
        </Aviso>
      )}
    </div>
  );
}

/* ─── Edição ──────────────────────────────────────────────────────────────── */

/**
 * Editar o vínculo: nome, telefone, vínculo trabalhista, papel, assinatura.
 *
 * Só o que mudou é enviado — mandar o formulário inteiro faria a API regravar
 * campos iguais e esconderia, no histórico, o que de fato foi alterado.
 *
 * O acesso tem dois caminhos, separados do "Salvar":
 *
 * - **Enviar link para criar a senha** — botão próprio, com confirmação, porque
 *   manda e-mail. Não troca nada: a senha atual vale até a pessoa criar a nova.
 * - **Nova senha** — só para a operação (admin), como no cadastro. Vai junto do
 *   "Salvar" e vale na hora; a API recusa para quem não é admin, porque a conta
 *   é a mesma em todas as empresas da pessoa.
 */
export function EdicaoDeColaborador(props: {
  readonly businessId: string;
  readonly colaborador: Colaborador | null;
  readonly aoFechar: () => void;
  /** Como no cadastro: `false` para o assessor, que não nomeia gestor nem signatário. */
  readonly podeDefinirPapeis?: boolean;
}): JSX.Element {
  const definePapeis = props.podeDefinirPapeis !== false;
  const editar = useEditarColaborador(props.businessId);
  // Uma mutação à parte: o resultado do link não se confunde com o do "Salvar".
  const enviarLink = useEditarColaborador(props.businessId);
  const { confirmar } = useDialogo();
  const admin = useIdentidade()?.isAdmin === true;
  const c = props.colaborador;

  // `null` = "não mexeu"; o que não mexeu não é enviado.
  const [nome, setNome] = useState<string | null>(null);
  const [telefone, setTelefone] = useState<string | null>(null);
  const [vinculos, setVinculos] = useState<string[] | null>(null);
  const [gestor, setGestor] = useState<boolean | null>(null);
  const [assina, setAssina] = useState<boolean | null>(null);
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);

  const fechar = (): void => {
    editar.reset();
    enviarLink.reset();
    setSenha('');
    setMostrarSenha(false);
    setNome(null);
    setTelefone(null);
    setVinculos(null);
    setGestor(null);
    setAssina(null);
    props.aoFechar();
  };

  const nomeAtual = nome ?? c?.fullName ?? '';
  const telefoneAtual = telefone ?? formatarTelefone(c?.phone ?? '');
  const vinculosAtuais = vinculos ?? separarVinculos(c?.occupation);
  const gestorAtual = gestor ?? c?.papel === 'Gestor';
  const assinaAtual = assina ?? c?.assinaPelaEmpresa ?? false;
  const foraDaLista = vinculosForaDaLista(c?.occupation);

  const salvar = (): void => {
    if (c === null) return;

    editar.mutate(
      {
        accountId: c.accountId,
        dados: {
          ...(nome === null || nome === c.fullName ? {} : { fullName: nome }),
          ...(telefone === null ? {} : { phone: telefone }),
          ...(vinculos === null ? {} : { occupation: juntarVinculos(vinculos) }),
          ...(gestor === null || gestor === (c.papel === 'Gestor') ? {} : { gestor }),
          ...(assina === null || assina === c.assinaPelaEmpresa
            ? {}
            : { assinaPelaEmpresa: assina }),
          ...(admin && senha !== '' ? { senha } : {}),
        },
      },
      { onSuccess: fechar },
    );
  };

  const linkEnviado = enviarLink.data?.credenciais;

  return (
    <Modal
      key={c?.accountId ?? 'nenhum'}
      aberto={c !== null}
      aoFechar={fechar}
      titulo="Editar colaborador"
      descricao={c?.emailAddress}
      largura="larga"
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao onClick={salvar} carregando={editar.isPending}>
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo rotulo="Nome" value={nomeAtual} onChange={(e) => setNome(e.target.value)} />
        <CampoTelefone
          rotulo="Telefone"
          ajuda="Opcional. Apague para remover."
          value={telefoneAtual}
          onChange={setTelefone}
        />
        <SeletorDeVinculos escolhidos={vinculosAtuais} aoMudar={setVinculos} />
        {foraDaLista.length > 0 && vinculos === null && (
          <Aviso tom="atencao" titulo="Vínculo gravado fora da lista">
            O cadastro tem &quot;{foraDaLista.join(', ')}&quot;, que veio de texto livre. Ao
            escolher na lista acima, esse valor é substituído.
          </Aviso>
        )}

        {definePapeis && (
          <>
            <Caixa
              rotulo="Gestor da empresa"
              ajuda="Enxerga e administra tudo da empresa, inclusive outros colaboradores."
              marcado={gestorAtual}
              aoMudar={setGestor}
            />
            <Caixa
              rotulo="Assina pela empresa"
              ajuda="Vai no contrato como signatário. Marcar quem não pode assinar faz o contrato voltar sem valor."
              marcado={assinaAtual}
              aoMudar={setAssina}
            />
          </>
        )}

        <fieldset className="flex flex-col gap-3 border-t border-borda pt-4">
          <legend className="mb-1 text-xs uppercase tracking-wide text-conteudo-sutil">
            Acesso ao sistema
          </legend>
          <div>
            <Botao
              variante="secundario"
              tamanho="pequeno"
              carregando={enviarLink.isPending}
              onClick={async () => {
                if (c === null) return;
                const ok = await confirmar({
                  titulo: `Enviar o link para ${c.emailAddress}?`,
                  descricao:
                    'A pessoa recebe um link para criar a própria senha, que vale 7 dias e só uma vez. A senha atual continua valendo até ela criar a nova.',
                  rotuloConfirmar: 'Enviar link',
                });
                if (ok) {
                  enviarLink.mutate({ accountId: c.accountId, dados: { enviarLinkDeSenha: true } });
                }
              }}
            >
              Enviar link para criar a senha
            </Botao>
            <p className="mt-1 text-xs text-conteudo-sutil">
              Por e-mail, para {c?.emailAddress}. Não troca a senha atual.
            </p>
          </div>
          {linkEnviado !== undefined &&
            (linkEnviado.enviado ? (
              <Aviso tom="positivo" titulo="Link enviado">
                {c?.emailAddress} recebeu o link para criar a senha. Ele vale por 7 dias.
              </Aviso>
            ) : (
              <Aviso tom="atencao" titulo="O e-mail não saiu">
                {linkEnviado.motivo ?? 'O provedor de e-mail recusou o envio.'} Tente de novo em
                instantes.
              </Aviso>
            ))}
          {enviarLink.isError && (
            <ErroDaApi titulo="Não foi possível enviar o link" erro={enviarLink.error} />
          )}

          {admin && (
            <Campo
              rotulo="Nova senha"
              type={mostrarSenha ? 'text' : 'password'}
              autoComplete="new-password"
              ajuda="Só a operação define a senha por aqui. Vale ao salvar: a atual deixa de funcionar e as sessões abertas da pessoa são encerradas. Combine a nova com ela por um canal seguro."
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              sufixo={
                <button
                  type="button"
                  className="text-xs text-conteudo-secundario underline-offset-2 hover:underline"
                  aria-pressed={mostrarSenha}
                  onClick={() => setMostrarSenha((v) => !v)}
                >
                  {mostrarSenha ? 'Ocultar' : 'Mostrar'}
                </button>
              }
            />
          )}
        </fieldset>

        {editar.isSuccess && (
          <Aviso tom="positivo" titulo="Colaborador atualizado">
            As mudanças valem a partir de agora.
          </Aviso>
        )}
        {editar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={editar.error} />}
      </div>
    </Modal>
  );
}

/* ─── Controles ───────────────────────────────────────────────────────────── */

/** Caixa de marcação com rótulo clicável e ajuda associada. */
function Caixa(props: {
  readonly rotulo: string;
  readonly ajuda?: string;
  readonly marcado: boolean;
  readonly aoMudar: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={props.marcado}
        onChange={(e) => props.aoMudar(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
      />
      <span className="flex flex-col">
        <span className="text-sm text-conteudo-primario">{props.rotulo}</span>
        {props.ajuda !== undefined && (
          <span className="text-xs text-conteudo-sutil">{props.ajuda}</span>
        )}
      </span>
    </label>
  );
}
